import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { persistStorage } from '@/tutorial/storage';

export interface CrmTenantConfig {
  id: string;
  name: string;
  slug: string;
  status: string;
  subdomain?: string;
  appName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  layoutType?: string;
  adminUrl?: string;
  clientUrl?: string;
  features: Record<string, boolean>;
}

interface TenantStore {
  tenant: CrmTenantConfig | null;
  isResolved: boolean;
  isLoading: boolean;
  error: string | null;
  setTenant: (tenant: CrmTenantConfig) => void;
  mergeTenant: (partial: Partial<CrmTenantConfig>) => void;
  /**
   * Clear the resolved tenant back to the unresolved state. Called on logout /
   * admin-session switch so the persisted tenant never leaks into the next
   * session/account (a stale tenant.id would be sent as X-Tenant-Id). Leaving
   * `isResolved:false` makes TenantProvider re-resolve for the login screen.
   */
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  isFeatureEnabled: (key: string) => boolean;
}

export const useTenantStore = create<TenantStore>()(
  persist(
    (set, get) => ({
      tenant: null,
      isResolved: false,
      isLoading: true,
      error: null,

      setTenant: (tenant) => set({ tenant, isResolved: true, isLoading: false, error: null }),

      reset: () => set({ tenant: null, isResolved: false, isLoading: false, error: null }),

      mergeTenant: (partial) => {
        const { tenant } = get();
        if (tenant) {
          set({ tenant: { ...tenant, ...partial } });
        }
      },

      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error, isLoading: false }),

      isFeatureEnabled: (key: string) => {
        const { tenant } = get();
        if (!tenant) return false;
        if (key in tenant.features) return tenant.features[key];

        // CRM module toggles are commercial/security gates. Missing config must
        // fail closed; generic capabilities can remain backward-compatible.
        if (key.startsWith('crm_show_') || key === 'app_crm') return false;

        return true;
      },
    }),
    {
      name: 'opruscrm-tenant',
      storage: createJSONStorage(persistStorage),
      // Persist ONLY the resolved tenant so it survives a reload on its own
      // (immediate branding/feature-flags/X-Tenant-Id, no null flash). The
      // transient resolution flags (isResolved/isLoading/error) stay in-memory
      // so TenantProvider still re-runs on reload to refresh branding + latest
      // data, and route guards that read `isResolved` synchronously keep their
      // exact current behavior (start unresolved until the resolve completes).
      partialize: (state) => ({ tenant: state.tenant }),
    }
  )
);
